import { Calendar } from 'lucide-react'
import { Link } from 'react-router-dom'
import type { InstagramReel } from '@domain/models/instagram-reel'

interface InstagramReelCardProps {
  reel: InstagramReel
}

function mediaKindLabel(value: InstagramReel['mediaKind']): string {
  if (value === 'reel') {
    return 'Reel'
  }

  if (value === 'post') {
    return 'Post'
  }

  return 'Content'
}

function formatTimestamp(value: Date | null): string {
  if (!value) {
    return 'Date unavailable'
  }

  return value.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })
}

export default function InstagramReelCard({ reel }: InstagramReelCardProps) {
  const contentLabel = mediaKindLabel(reel.mediaKind)
  const fallbackAriaLabel = reel.caption || reel.reelId

  return (
    <Link
      to={`/dr-michael-okun-my-take/${reel.reelId}`}
      aria-label={`Open Instagram ${contentLabel.toLowerCase()} details: ${fallbackAriaLabel}`}
      className="block overflow-hidden rounded-xl bg-card-surface no-underline shadow-lg shadow-black/20 ring-1 ring-black/5 transition-[transform,box-shadow] duration-200 hover:scale-[1.01] hover:shadow-lg hover:shadow-black/30"
    >
      {reel.displayImageUrl ? (
        <div className="overflow-hidden rounded-t-xl bg-border-light">
          <img
            src={reel.displayImageUrl}
            alt={reel.caption || `Instagram ${contentLabel.toLowerCase()} thumbnail`}
            className="aspect-square w-full object-cover"
            loading="lazy"
          />
        </div>
      ) : null}

      <div className="p-4">
        <div className="mb-2 flex items-start gap-2 text-xs font-semibold text-primary">
          <Calendar className="mt-0.5 h-3.5 w-3.5" aria-hidden />
          <time>{formatTimestamp(reel.timestamp)}</time>
        </div>

        <p className="line-clamp-3 text-sm text-text-primary">
          {reel.caption || 'No caption available.'}
        </p>

        <div className="mt-4 flex items-center gap-2">
          <span className="inline-flex max-w-full rounded-full bg-blue-light px-2.5 py-0.5 text-center text-xs font-bold leading-tight text-blue">
            {contentLabel}
          </span>
          <span className="inline-flex max-w-full rounded-full bg-blue-light px-2.5 py-0.5 text-center text-xs font-bold leading-tight text-blue">
            Open details
          </span>
        </div>
      </div>
    </Link>
  )
}
